/**
 * Composable per la gestione responsive basata sui breakpoint di TailwindCSS
 *
 * Questo composable fornisce funzionalità reattive per gestire i cambiamenti di dimensione
 * della finestra del browser e fornisce computed properties e funzioni utility per
 * controllare la visibilità degli elementi in base ai breakpoint.
 *
 * Versione Composition API del mixin responsive esistente - mantiene piena compatibilità
 * comportamentale e permette uso in script setup.
 *
 * @example
 * // Uso in script setup:
 * import { useResponsive } from '@/composables/useResponsive';
 *
 * const {
 *   showFromMd,
 *   showOnlyXs,
 *   currentBreakpoint,
 *   forceTableRerender
 * } = useResponsive();
 *
 * @example
 * // Nel template:
 * <div v-if="showFromLg">Visibile solo su schermi large e superiori</div>
 * <div v-if="showOnlyMd">Visibile solo su schermi medium</div>
 *
 * @author Sistema Timecard
 * @version 1.0.0
 */

import { ref, computed, onMounted, onUnmounted, nextTick, getCurrentInstance, readonly } from 'vue';
import type { Ref, ComputedRef } from 'vue';
import { ResponsiveUtils } from '@/lib/utils/responsiveUtils';
import type { BreakpointConfig, BreakpointKey } from '@/lib/utils/responsiveUtils';

// Nota: BreakpointConfig ora importato da ResponsiveUtils per evitare duplicazione

/**
 * Tipo di ritorno del composable useResponsive
 */
export interface UseResponsiveReturn {
	// Dati reattivi
	windowWidth: Readonly<Ref<number>>;
	tableKey: Readonly<Ref<number>>;
	breakpoints: BreakpointConfig;

	// Computed properties - "da breakpoint in su"
	showFromXs: ComputedRef<boolean>;
	showFromSm: ComputedRef<boolean>;
	showFromMd: ComputedRef<boolean>;
	showFromLg: ComputedRef<boolean>;
	showFromXl: ComputedRef<boolean>;
	showFrom2xl: ComputedRef<boolean>;

	// Computed properties - "solo in questo range"
	showOnlyXs: ComputedRef<boolean>;
	showOnlySm: ComputedRef<boolean>;
	showOnlyMd: ComputedRef<boolean>;
	showOnlyLg: ComputedRef<boolean>;
	showOnlyXl: ComputedRef<boolean>;

	// Breakpoint corrente
	currentBreakpoint: ComputedRef<BreakpointKey>;

	// Metodi utility
	updateWindowWidth: () => void;
	forceTableRerender: () => void;
}

/**
 * Composable per la gestione responsive
 *
 * Fornisce computed properties reattive per controllare la visibilità degli elementi
 * in base alla larghezza della finestra e ai breakpoint di TailwindCSS.
 *
 * Include anche funzionalità per forzare il re-render delle tabelle Element-UI
 * quando cambiano i breakpoint critici.
 *
 * @returns Oggetto con tutte le proprietà e metodi responsive
 */
export function useResponsive(): UseResponsiveReturn {
	// Dati reattivi
	const windowWidth = ref<number>(0);
	const tableKey = ref<number>(0);
	let resizeTimeout: NodeJS.Timeout | null = null;

	// Configurazione breakpoints centralizzata da ResponsiveUtils
	const breakpoints: BreakpointConfig = ResponsiveUtils.getBreakpoints();

	// Computed properties usando ResponsiveUtils per logica centralizzata
	const showFromXs = computed(() =>
		ResponsiveUtils.isBreakpointActiveFrom('xs', windowWidth.value),
	);
	const showFromSm = computed(() =>
		ResponsiveUtils.isBreakpointActiveFrom('sm', windowWidth.value),
	);
	const showFromMd = computed(() =>
		ResponsiveUtils.isBreakpointActiveFrom('md', windowWidth.value),
	);
	const showFromLg = computed(() =>
		ResponsiveUtils.isBreakpointActiveFrom('lg', windowWidth.value),
	);
	const showFromXl = computed(() =>
		ResponsiveUtils.isBreakpointActiveFrom('xl', windowWidth.value),
	);
	const showFrom2xl = computed(() =>
		ResponsiveUtils.isBreakpointActiveFrom('2xl', windowWidth.value),
	);

	// Computed properties per controlli "solo in questo range"
	const showOnlyXs = computed(() =>
		ResponsiveUtils.isBreakpointActiveOnly('xs', windowWidth.value),
	);
	const showOnlySm = computed(() =>
		ResponsiveUtils.isBreakpointActiveOnly('sm', windowWidth.value),
	);
	const showOnlyMd = computed(() =>
		ResponsiveUtils.isBreakpointActiveOnly('md', windowWidth.value),
	);
	const showOnlyLg = computed(() =>
		ResponsiveUtils.isBreakpointActiveOnly('lg', windowWidth.value),
	);
	const showOnlyXl = computed(() =>
		ResponsiveUtils.isBreakpointActiveOnly('xl', windowWidth.value),
	);

	// Nome del breakpoint corrente
	const currentBreakpoint = computed(
		(): BreakpointKey => ResponsiveUtils.getCurrentBreakpoint(windowWidth.value),
	);

	// Metodi utility
	function updateWindowWidth(): void {
		windowWidth.value = window.innerWidth;
	}

	function shouldForceTableRerender(oldWidth: number, newWidth: number): boolean {
		return ResponsiveUtils.shouldForceTableRerender(oldWidth, newWidth);
	}

	function forceTableRerender(): void {
		tableKey.value++;
		nextTick(() => {
			// Accesso sicuro all'istanza Vue per $refs
			const instance = getCurrentInstance();
			if (instance?.proxy?.$refs) {
				const searchTable = (instance.proxy.$refs as any).searchTable;
				if (searchTable && typeof searchTable.doLayout === 'function') {
					setTimeout(() => {
						searchTable.doLayout();
					}, 0);
				}
			}
		});
	}

	function handleResponsiveResize(): void {
		if (resizeTimeout) {
			clearTimeout(resizeTimeout);
		}
		resizeTimeout = setTimeout(() => {
			const oldWidth = windowWidth.value;
			updateWindowWidth();

			// Force table re-render se cambiano breakpoint critici
			if (shouldForceTableRerender(oldWidth, windowWidth.value)) {
				forceTableRerender();
			}
		}, 150);
	}

	// Setup lifecycle (equivalente a mounted/beforeDestroy del mixin)
	onMounted(() => {
		updateWindowWidth();
		window.addEventListener('resize', handleResponsiveResize);

		// Force table rerender after responsive is ready
		nextTick(() => {
			forceTableRerender();
		});
	});

	onUnmounted(() => {
		window.removeEventListener('resize', handleResponsiveResize);
		// Clear timeout if still pending
		if (resizeTimeout) {
			clearTimeout(resizeTimeout);
		}
	});

	// Return API pubblica (stessa struttura del mixin)
	return {
		// Dati reattivi (readonly per sicurezza)
		windowWidth: readonly(windowWidth),
		tableKey: readonly(tableKey),
		breakpoints,

		// Computed properties "da breakpoint in su"
		showFromXs,
		showFromSm,
		showFromMd,
		showFromLg,
		showFromXl,
		showFrom2xl,

		// Computed properties "solo in questo range"
		showOnlyXs,
		showOnlySm,
		showOnlyMd,
		showOnlyLg,
		showOnlyXl,

		// Breakpoint corrente
		currentBreakpoint,

		// Metodi utility
		updateWindowWidth,
		forceTableRerender,
	};
}

/**
 * Type guard per verificare se un oggetto ha le proprietà responsive
 */
export function isResponsiveObject(obj: any): obj is UseResponsiveReturn {
	return (
		obj &&
		typeof obj.windowWidth !== 'undefined' &&
		typeof obj.showFromMd !== 'undefined' &&
		typeof obj.currentBreakpoint !== 'undefined'
	);
}
